/** The breadcrumb for a route: the job, and a Settings sheet, an event (by `eventName`, once known) or a Labs entry under it. People is one crumb: its view switcher names the view, so the trail does not say it twice. Every first crumb carries its nav icon. The trail names places, never the product. */
import type { IconName } from "../kit";
import { labsEntry } from "../screens/labs/entries";
import { jobFor } from "./jobs";
import { href } from "./links";

export interface Crumb {
  label: string;
  icon?: IconName;
  href?: string;
}

/** The Settings sheets, by their `?open=` key. */
const SETTINGS: Record<string, string> = { users: "Users", sync: "Data and sync", activity: "Sync activity", quality: "Data quality", exports: "Exports", audit: "Audit", reports: "My reports" };

/** The event a page is about (`/events/<ref>`), or null. */
export function eventRefIn(pathname: string): string | null {
  const match = /^\/events\/([^/]+)$/.exec(pathname);
  return match ? decodeURIComponent(match[1] as string) : null;
}

export function crumbs(pathname: string, search: string, eventName?: string): Crumb[] {
  const params = new URLSearchParams(search);
  const job = jobFor(pathname);
  if (job?.to === "/events" && eventRefIn(pathname)) return [{ label: job.label, icon: job.icon, href: href(job.to) }, { label: eventName ?? "Event" }];
  if (job?.to === "/settings") {
    if (pathname === "/settings/imports") return [{ label: job.label, icon: job.icon, href: href(job.to) }, { label: "Imports" }];
    const open = SETTINGS[params.get("open") ?? ""];
    if (open) return [{ label: job.label, icon: job.icon, href: href(job.to) }, { label: open }];
  }
  if (job) return [{ label: job.label, icon: job.icon }];
  if (pathname.startsWith("/labs")) return [{ label: "Labs", icon: "dashboard", href: href("/") }, { label: labsEntry(pathname.split("/")[2])?.name ?? "Not found" }];
  return [{ label: "Not found" }];
}

/** The tab's title: the page's place, then the product. */
export function pageTitle(pathname: string): string {
  return `${jobFor(pathname)?.label ?? (pathname.startsWith("/labs") ? "Labs" : "Not found")} · Keystone`;
}
